import { QueryClient, UseQueryResult, useQuery } from '@tanstack/react-query';
import { RecurrenceEnum } from 'routes/HumanResourcesModule/Settings/HrPositionMapping/helpers/constants';
import { SIMULATION_ENDPOINT_PREFIX } from 'routes/HumanResourcesModule/Simulation/helpers/constants';
import { RQAPICallWithData } from 'store/APIClient';
import { Response } from 'routes/HumanResourcesModule/business/types';
import { SimulationAgentData } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/helpers/types';
import { invalidateAgentQueries } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/Agents/components/PayComponent/helpers/valueByDateHelpers';
import { useLocalStorage } from 'helpers/reactUseHelper';
import { GridEchelons } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/types';
import { useGetGridEchelons } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/EchelonsOverview/helpers/hooks/echelons';
import { useEffect } from 'react';
import { useGetHrFreeHypotheses } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/freeHypothesis';
import { HrSimulation } from 'routes/HumanResourcesModule/Simulation/helpers/types';
import { usePostIFSEInformationMany } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/components/FreeHypothesis/helpers/hooks/ifseInformation';

const HR_SIMULATION_AGENTS_DATA_API_KEY = 'hr_simulation_agents_data';
export const HR_SIMULATION_NON_RECURRENT_ENVELOPE_DATA_API_KEY =
    'hr_simulation_non_recurrent_envelope_data';

export const resetSimulationAgentDataQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
    recurrence: RecurrenceEnum,
) => {
    queryClient.resetQueries({
        queryKey: [
            HR_SIMULATION_AGENTS_DATA_API_KEY,
            hrSimulationId,
            recurrence,
        ],
    });
};

export const invalidateSimulationAgentDataQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
    recurrence: RecurrenceEnum,
) => {
    queryClient.invalidateQueries({
        queryKey: [
            HR_SIMULATION_AGENTS_DATA_API_KEY,
            hrSimulationId,
            recurrence,
        ],
    });
};

/**
 * This function invalidate all Agent data, independently of the recurrence. Its alos refetching the historical data.
 */
export const invalidateAllSimulationAgentDataQueries = (
    hrSimulationId: number,
    queryClient: QueryClient,
) => {
    invalidateAgentQueries(queryClient);
    queryClient.invalidateQueries({
        queryKey: [HR_SIMULATION_AGENTS_DATA_API_KEY, hrSimulationId],
    });
};

export const getSimulationAgentDataFromCache = (
    queryClient: QueryClient,
    hrSimulationId: number,
    recurrence: RecurrenceEnum,
): Response<SimulationAgentData> | undefined =>
    queryClient.getQueryData<Response<SimulationAgentData>>([
        HR_SIMULATION_AGENTS_DATA_API_KEY,
        hrSimulationId,
        recurrence,
    ]);

export const setSimulationAgentDataFromCache = (
    queryClient: QueryClient,
    hrSimulationId: number,
    recurrence: RecurrenceEnum,
    updatedData: Response<SimulationAgentData>,
) =>
    queryClient.setQueryData<Response<SimulationAgentData>>(
        [HR_SIMULATION_AGENTS_DATA_API_KEY, hrSimulationId, recurrence],
        updatedData,
    );

export const useGetHrSimulationAgentData = (
    hrSimulationId: number,
    recurrence: RecurrenceEnum,
): UseQueryResult<Response<SimulationAgentData>> =>
    useQuery({
        queryKey: [
            HR_SIMULATION_AGENTS_DATA_API_KEY,
            hrSimulationId,
            recurrence,
        ],
        queryFn: async () => {
            const response = await RQAPICallWithData<
                Response<SimulationAgentData>
            >({
                endpoint: `${SIMULATION_ENDPOINT_PREFIX}/${hrSimulationId}/${recurrence}/simulation_data`,
            });
            return response.content.data;
        },
    });

export const useGetOfficialGradesOfLocalStorage = (): GridEchelons[] => {
    const [echelonGrades, setEchelonGrades] = useLocalStorage<GridEchelons[]>(
        'echelonGrades',
        [],
    );
    const { data: echelonGradesResponse, isSuccess } = useGetGridEchelons({
        shouldFetch: echelonGrades === undefined || echelonGrades.length === 0,
    });

    useEffect(() => {
        if (isSuccess) {
            setEchelonGrades(echelonGradesResponse);
        }
    }, [isSuccess]);

    return echelonGrades ?? [];
};

export const useAgentAlreadyAffectedByIFSEHypothesis = (
    simId: HrSimulation['id'],
    agentSoftwareId: string,
    enabled: boolean = true,
) => {
    const { data: freeHypotheses } = useGetHrFreeHypotheses(simId, enabled);
    const { data: hypothesesInfo } = usePostIFSEInformationMany({
        simulationId: simId,
        hypotheses:
            freeHypotheses?.map((h) => ({
                filters: h.filters,
                id: h.id,
            })) ?? [],
        enabled: enabled && freeHypotheses !== undefined,
    });
    if (!hypothesesInfo || !freeHypotheses)
        return {
            alreadyAffectedByIFSE: false,
            hypothesisAffectingAgent: undefined,
        };
    const hypothesisAffectingAgent = Object.keys(hypothesesInfo).find((hypId) =>
        hypothesesInfo[hypId].includes(agentSoftwareId),
    );
    return {
        alreadyAffectedByIFSE: hypothesisAffectingAgent !== undefined,
        hypothesisAffectingAgent: freeHypotheses.find(
            (h) => h.id.toString() === hypothesisAffectingAgent,
        ),
    };
};
